import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { BMP_DepositGroup, BMP_MonthlyInvestment, GetWalletResponse, MakeDepositResponse } from '../../models/bmp.model';
import { getBMPLiquidityList } from '../../api/getBMPLiquidityList';
import { getBMPWalletState } from '../../api/getBMPWalletState';
import { getBMPDepositGroup } from '../../api/getBMPDepositGroup';
import { InvestmentSelectionModal } from './modals/InvestmentSelectionModal';
import { GroupParticipantsModal } from './modals/GroupParticipantsModal';
import { responseHero } from '../../utils/axiosInterceptor';
import { formatCurrency } from './utils/formatCurrency';
import Decimal from 'decimal.js';
import { FixWalletComponent } from './wallets/FixWalletComponent';
import { RobotWalletComponent } from './wallets/RobotWalletComponent';
import { TradingWalletComponent } from './wallets/TradingWalletComponent';
import { DepositSuccessModal } from './wallets/modals/DepositSuccessModal';

export const DepositTable: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
    const [liquidityList, setLiquidityList] = useState<BMP_MonthlyInvestment[]>([]);
    const [walletState, setWalletState] = useState<GetWalletResponse | null>(null);
    const [groups, setGroups] = useState<BMP_DepositGroup[]>([]);
    
    const [isInvestModalOpen, setIsInvestModalOpen] = useState(false);
    const [successData, setSuccessData] = useState<MakeDepositResponse | null>(null);
    
    const [selectedGroup, setSelectedGroup] = useState<BMP_DepositGroup | null>(null);
    const [loading, setLoading] = useState(false);

    const [activeView, setActiveView] = useState<'main' | 'fix' | 'robot' | 'trading'>('main');

    // Új állapotok a Jegyzet (Note) Modal kezeléséhez
    const [noteGroup, setNoteGroup] = useState<BMP_DepositGroup | null>(null);
    const [noteText, setNoteText] = useState('');
    const [noteLoading, setNoteLoading] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [liquidityRes, walletRes, groupsRes] = await Promise.all([
                getBMPLiquidityList(),
                getBMPWalletState(),
                getBMPDepositGroup()
            ]);
            setLiquidityList(liquidityRes);
            setWalletState(walletRes);
            setGroups(groupsRes);
        } catch (err) {
            console.error("Failed to fetch data", err);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Amikor megnyitjuk a jegyzet modalt, betöltjük az eddigi jegyzetet, ha van
    const handleOpenNoteModal = (group: BMP_DepositGroup) => {
        setNoteGroup(group);
        // Feltételezve, hogy a group-ban van 'note' mező a backendről (pl: group.note)
        setNoteText(group?.note || ''); 
    };

    const handleSaveNote = async () => {
        if (!noteGroup) return;
        setNoteLoading(true);
        try {
            await responseHero.patch(`${process.env.REACT_APP_API_URL}/bmp/note/${noteGroup.id}`, {
                note: noteText
            });
            setNoteGroup(null);
            fetchData(); // Adatok frissítése, hogy látszódjon az új jegyzet, ha szükséges
        } catch (error: any) {
            alert(`Hiba a jegyzet mentése során: ${error.response?.data?.message || "Nem sikerült a mentés"}`);
        } finally {
            setNoteLoading(false);
        }
    };

    const totalPotential = useMemo(() => 
        liquidityList.reduce((acc, curr) => {
            const val = curr.investedUsd ? new Decimal(curr.investedUsd) : new Decimal(0);
            return acc.plus(val);
        }, new Decimal(0)), 
    [liquidityList]);

    const wallets = useMemo(() => {
        if (!walletState) return [];
        const { holdingsUsd: holdings, wallet } = walletState;
        const safeNum = (val: any) => val ? new Decimal(val).toNumber() : 0;

        return [
            { 
                id: 'fix' as const,
                title: "FIX BTC & ETH", 
                subtitle: "Cold Storage (Ledger)", 
                value: safeNum(holdings?.fixBtc) + safeNum(holdings?.fixEth), 
                accent: "border-amber-200", 
                bg: "bg-amber-50/30", 
                share: "20%" 
            },
            { 
                id: 'robot' as const,
                title: "AI ROBOT", 
                subtitle: "Leveraged Strategy", 
                value: safeNum(holdings?.robotBtc) + safeNum(wallet?.robotLiquidity), 
                accent: "border-amber-400", 
                bg: "bg-amber-50/50", 
                share: "20%" 
            },
            { 
                id: 'trading' as const,
                title: "TRADING", 
                subtitle: "Spot Markets", 
                value: safeNum(holdings?.tradingBtc) + safeNum(holdings?.tradingEth) + safeNum(wallet?.tradingLiquidity), 
                accent: "border-amber-600", 
                bg: "bg-amber-600", 
                textColor: "text-white", 
                share: "60%" 
            }
        ];
    }, [walletState]);

    if (activeView === 'fix') {
        return (
            <FixWalletComponent 
                holdings={walletState?.holdingsUsd || null} 
                prices={walletState?.marketPrices || null}
                onBack={() => setActiveView('main')}
                onUpdate={fetchData}
            />
        );
    }
    
    if (activeView === 'robot') {
        return (
            <RobotWalletComponent 
                onBack={() => setActiveView('main')} 
                onUpdate={fetchData} 
            />
        );
    }
    
    if (activeView === 'trading') {
        return (
            <TradingWalletComponent 
                value={wallets[2].value} 
                onBack={() => setActiveView('main')} 
                onUpdate={fetchData} 
            />
        );
    }

    const handleSubmitSelection = async (selectedItems: BMP_MonthlyInvestment[], prices: { btc: number; eth: number, liq: number }) => {
        setLoading(true);
        try {
            const payload = {
                btcPrice: prices.btc,
                ethPrice: prices.eth,
                liquidityShare: prices.liq,
                participants: selectedItems 
            };
            
            const response = await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/deposit/make`, payload);
            
            setIsInvestModalOpen(false);
            setSuccessData(response.data); 
            fetchData(); 
        } catch (error: any) {
            alert(`Hiba: ${error.response?.data?.message || "Nem sikerült a mentés"}`);
        } finally { setLoading(false); }
    };

    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-8 antialiased">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <button onClick={onBack} className="text-sm font-semibold text-amber-900/60 hover:text-amber-900 transition-colors">← Vissza</button>
                {walletState?.marketPrices && (
                    <div className="flex gap-4 bg-white border border-amber-100 px-4 py-2 rounded-xl shadow-sm text-sm font-bold text-slate-700">
                        <span><span className="text-amber-500 mr-2 uppercase text-[10px]">BTC</span>{formatCurrency(walletState.marketPrices.btc)}</span>
                        <div className="w-px h-4 bg-amber-100" />
                        <span><span className="text-amber-500 mr-2 uppercase text-[10px]">ETH</span>{formatCurrency(walletState.marketPrices.eth)}</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <div className="lg:col-span-2 bg-white border border-amber-100 p-8 rounded-3xl shadow-sm flex flex-col justify-center">
                    <h2 className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-2">Összesített Portfólió Érték</h2>
                    <div className="text-5xl font-black text-slate-900 tracking-tight">
                    {walletState?.totalValueUsd 
                    ? formatCurrency(new Decimal(walletState.totalValueUsd).toNumber()) 
                    : '---'}
                    </div>
                </div>
                <button onClick={() => setIsInvestModalOpen(true)} className="bg-amber-900 hover:bg-amber-950 text-white p-8 rounded-3xl transition-all shadow-lg flex flex-col justify-center group">
                    <span className="text-amber-300/80 text-[10px] font-bold uppercase tracking-widest mb-1">Befektetésre vár</span>
                    <span className="text-3xl font-bold">{formatCurrency(totalPotential.toNumber())}</span>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {wallets.map((w, i) => (
                    <div 
                        key={i} 
                        onClick={() => setActiveView(w.id)}
                        className={`group cursor-pointer border ${w.accent} ${w.bg} ${w.textColor || 'text-slate-800'} 
                        p-6 rounded-2xl transition-all duration-300 transform 
                        hover:-translate-y-2 hover:shadow-xl active:scale-95`}
                    >
                        <div className="flex justify-between items-center mb-4 italic text-[10px] uppercase font-bold opacity-70">
                            <span>{w.title}</span>
                            <span className="group-hover:translate-x-1 transition-transform">→ {w.share}</span>
                        </div>
                        <div className="text-2xl font-bold">{walletState ? formatCurrency(w.value) : '...'}</div>
                        <div className="mt-4 text-[9px] font-bold uppercase tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity">
                            Részletek megtekintése
                        </div>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-amber-50 rounded-3xl shadow-sm overflow-hidden">
                <div className="p-8 border-b border-amber-50">
                    <h3 className="text-lg font-bold text-slate-800">Befektetési Előzmények</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-amber-50/50 text-[10px] font-bold uppercase tracking-widest text-amber-900/60">
                                <th className="px-8 py-4">ID</th>
                                <th className="px-8 py-4">Dátum</th>
                                <th className="px-8 py-4 text-right">Összeg (USD)</th>
                                <th className="px-8 py-4 text-right">BTC / ETH Ár</th>
                                <th className="px-8 py-4 text-center">Akciók</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-50">
                            {groups.map((group) => (
                                <tr key={group.id} className="hover:bg-amber-50/20 transition-colors group">
                                    <td className="px-8 py-5 text-sm font-mono text-slate-400">#{group.id}</td>
                                    <td className="px-8 py-5 text-sm font-medium text-slate-700">
                                        {new Date(group.date).toLocaleDateString('hu-HU')}
                                    </td>
                                    <td className="px-8 py-5 text-sm font-bold text-right text-slate-900">
                                        {group.groupDepositUsd ? new Decimal(group.groupDepositUsd || 0).toNumber().toLocaleString() : '0'}
                                    </td>
                                    <td className="px-8 py-5 text-xs text-right text-slate-500 font-medium">
                                        <div className="flex flex-col">
                                            <span>B: {group.bitcoinExchangeRate ? new Decimal(group.bitcoinExchangeRate).toNumber().toLocaleString() : '0'}</span>
                                            <span>E: {group.ethereumExchangeRate ? new Decimal(group.ethereumExchangeRate).toNumber().toLocaleString() : '0'}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center justify-center gap-2">
                                            <button 
                                                onClick={() => setSelectedGroup(group)}
                                                className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-900 text-xs font-bold hover:bg-amber-200 transition-colors"
                                            >
                                                Részletek
                                            </button>
                                            <button 
                                                onClick={() => handleOpenNoteModal(group)}
                                                className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors flex items-center gap-1"
                                            >
                                                Jegyzet
                                            </button>
                                        </div>
                                        {/* Opcionális: kis ikon vagy szöveg, ha már van mentett jegyzet */}
                                        {group?.note && (
                                            <div className="text-center mt-1 text-[10px] text-slate-400 italic truncate max-w-[150px] mx-auto">
                                                "{group.note}"
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* JEGYZET (NOTE) MODAL WINDOW */}
            {noteGroup && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white border border-amber-100 rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
                        <div className="p-6 border-b border-amber-50 flex justify-between items-center bg-amber-50/30">
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Jegyzet szerkesztése (#{noteGroup.id})</h3>
                            <button 
                                onClick={() => setNoteGroup(null)} 
                                className="text-slate-400 hover:text-slate-600 font-bold text-sm"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6">
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value.slice(0, 150))} // 150 karakternél vágja le
                                placeholder="Írd ide a jegyzetet..."
                                rows={4}
                                className="w-full p-3 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none"
                            />
                            <div className="flex justify-between items-center mt-2">
                                <span className={`text-[11px] font-semibold ${noteText.length >= 150 ? 'text-red-500' : 'text-slate-400'}`}>
                                    {noteText.length} / 150 karakter
                                </span>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                            <button
                                onClick={() => setNoteGroup(null)}
                                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors"
                                disabled={noteLoading}
                            >
                                Mégse
                            </button>
                            <button
                                onClick={handleSaveNote}
                                className="px-4 py-2 bg-amber-900 hover:bg-amber-950 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
                                disabled={noteLoading}
                            >
                                {noteLoading ? 'Mentés...' : 'Mentés'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <InvestmentSelectionModal 
                isOpen={isInvestModalOpen} 
                onClose={() => setIsInvestModalOpen(false)} 
                onSubmit={handleSubmitSelection} 
                data={liquidityList} 
                isLoading={loading} 
            />

            <GroupParticipantsModal 
                group={selectedGroup} 
                onClose={() => setSelectedGroup(null)} 
            />

            <DepositSuccessModal 
                data={successData} 
                onClose={() => setSuccessData(null)} 
            />
        </div>
    );
};